import { CompactText } from 'ui/CompactText';
import { useState, type FormEvent } from 'react';

import { addNotification } from 'notifications';
import { sendSupportMessage } from 'api/v1/requests/support';

import classes from './SupportContactForm.module.scss';

type Props = {
  roleLabel: 'ученик' | 'репетитор';
  replyEmail?: string;
  onSent?: (payload: { subject: string; body: string }) => void;
  onCancel?: () => void;
};

export const SupportContactForm = ({ roleLabel, replyEmail, onSent, onCancel }: Props) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Ошибки проверки показываем под полем (.fig-field-error), а не всплывашкой.
  const [errors, setErrors] = useState<{ subject?: string; body?: string }>({});
  const you = roleLabel === 'ученик';

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const topic = subject.trim();
    const text = body.trim();
    const nextErrors: { subject?: string; body?: string } = {};
    if (topic.length < 3) {
      nextErrors.subject = you
        ? 'Укажи тему обращения - хотя бы несколько слов.'
        : 'Укажите тему обращения - хотя бы несколько слов.';
    }
    if (text.length < 10) {
      nextErrors.body = you
        ? 'Опиши вопрос подробнее, чтобы мы смогли помочь.'
        : 'Опишите вопрос подробнее, чтобы мы смогли помочь.';
    }
    setErrors(nextErrors);
    if (nextErrors.subject || nextErrors.body) return;
    setSubmitting(true);
    try {
      await sendSupportMessage({ subject: topic, body: text });
      setSubject('');
      setBody('');
      if (onSent) {
        onSent({ subject: topic, body: text });
      } else {
        addNotification(
          you
            ? 'Письмо отправлено в поддержку. Ответим на твою почту.'
            : 'Письмо отправлено в поддержку. Ответим на вашу почту.',
          'success',
        );
      }
    } catch {
      addNotification(
        'Не удалось отправить письмо. Напишите на support@myteacher.pro.',
        'error',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      <label className={classes.field}>
        <span>Тема обращения</span>
        <input
          className={classes.control}
          value={subject}
          onChange={(event) => {
            setSubject(event.target.value);
            if (errors.subject) setErrors((prev) => ({ ...prev, subject: undefined }));
          }}
          placeholder='Тема'
          disabled={submitting}
          aria-invalid={errors.subject ? true : undefined}
        />
        {errors.subject ? (
          <span className='fig-field-error' role='alert'>
            {errors.subject}
          </span>
        ) : null}
      </label>
      <label className={classes.field}>
        <span>Сообщение</span>
        <textarea
          className={classes.area}
          value={body}
          onChange={(event) => {
            setBody(event.target.value);
            if (errors.body) setErrors((prev) => ({ ...prev, body: undefined }));
          }}
          aria-invalid={errors.body ? true : undefined}
          placeholder={
            you
              ? 'Опиши вопрос. Мы увидим, что ты ученик, и ответим на почту аккаунта.'
              : `Опишите вопрос. Мы увидим, что вы ${roleLabel}, и ответим на почту аккаунта.`
          }
          disabled={submitting}
        />
        {errors.body ? (
          <span className='fig-field-error' role='alert'>
            {errors.body}
          </span>
        ) : null}
      </label>
      {replyEmail ? (
        <>
          <p className={classes.metaLabel}>Почта для ответа</p>
          <p className={classes.metaValue}>{replyEmail}</p>
        </>
      ) : null}
      <CompactText className={classes.hint} tone={'info'}>
        Не присылай пароль, коды или платежные данные.
      </CompactText>
      <div className={classes.actions}>
        <button type='submit' className={classes.primary} disabled={submitting}>
          {submitting ? 'Отправка...' : 'Отправить обращение'}
        </button>
        {onCancel ? (
          <button type='button' className={classes.secondary} onClick={onCancel} disabled={submitting}>
            Отмена
          </button>
        ) : null}
      </div>
    </form>
  );
};
